/* LAV TDMS — Портал подрядчика. Урезанное рабочее место: подрядчик видит свои
   поставки, подаёт входящий трансмиттал заказчику и отвечает на замечания.

   Границы видимости держит сервер по вендору из токена. Раньше портал получал
   в память проект целиком и отфильтровывал чужое сам — то есть «изоляция»
   существовала ровно до открытия консоли браузера. */
import { useEffect, useState, useMemo, type ReactNode, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { AgreedCode, Crs, Doc, Mdr, Purpose as PurposeCode, ReviewCode, Transmittal, Vdoc, Vdr } from '@/data/types'
import { useList, useSub, qk, inboxKeys } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { noteExport, uploadForm } from '@/data/api'
import { exportDocs, exportTransmittals } from '@/lib/excel'
import { useDictLabels, useDictRows } from '@/data/meta'
import type { Tone } from '@/ui'
import { clickable, toast } from '@/ui'
import {
  Badge,
  Button,
  Avatar,
  Input,
  Select,
  Toolbar,
  Checkbox,
  Dialog,
  CodeChip,
  LifeBadge,
  DiscTag,
  Crit,
  SlaPill,
  Purpose,
  PageHead,
  EmptyState,
  Field,
  SmartTable,
  Spinner,
  type SmartColumn,
} from '@/ui'
import { Icon } from '@/ds/icons'
import { CritBadge, SheetPrint } from '@/screens/pt/CrsSheet'
import { CrsSheetRail, crsSheetTabs, useCrsSheets, useSheetItems, type CrsSheetRow } from '@/screens/pt/Comments'
import { CrsFilesZone, TrmFilesZone } from '@/screens/pt/TrmFiles'
import { Pager } from '@/screens/pt/Registers'
import { sliceFilters, type Slice } from '@/screens/pt/slices'
import { revVer } from '@/lib/format'
import { t, type Lang } from '@/i18n/lang'


/** Документ принадлежит либо реестру заказчика (MDR), либо реестру поставщика (VDR). */
const registerOf = (d: Doc | Vdoc): string => ('mdr' in d ? d.mdr : d.vdr)

/* Портал постраничный, как и реестр заказчика: у подрядчика по крупному
   контракту тысячи строк, и «первые 200 из 1340» — это предложение искать
   нужную строку глазами. Страница берётся с сервера, отбор и поиск — тоже.

   Сто строк на источник, а не двести: документы подрядчика лежат в двух
   реестрах (заказчика и поставки), и страница собирается из обоих. */
const PAGE = 100

/** Свои документы: часть лежит в реестре заказчика, часть — в реестре поставки. */
function useMyDocs(page: number, filters: Record<string, string[]>, q: string) {
  const query = { page, pageSize: PAGE, sort: [{ id: 'no', desc: false }], filters, q: q || undefined }
  const mdrSide = useList<Doc>('docs', query)
  const vdrSide = useList<Vdoc>('vdocs', query)
  const rows = useMemo<(Doc | Vdoc)[]>(
    () => [...(mdrSide.data?.rows ?? []), ...(vdrSide.data?.rows ?? [])],
    [mdrSide.data, vdrSide.data],
  )
  const total = (mdrSide.data?.total ?? 0) + (vdrSide.data?.total ?? 0)
  return {
    rows,
    total,
    /* Страниц столько, сколько у более длинного источника: короткий кончится
       раньше и просто перестанет добавлять строки. */
    pageCount: Math.max(mdrSide.data?.pageCount ?? 1, vdrSide.data?.pageCount ?? 1),
    isPending: mdrSide.isPending || vdrSide.isPending,
    error: mdrSide.error ?? vdrSide.error,
  }
}

/** Пока данные не пришли, экран не притворяется пустым. */
function Gate({ query, lang, children }: { query: { isPending: boolean; error: unknown }; lang: Lang; children: ReactNode }) {
  if (query.error) {
    return <EmptyState icon="alert-triangle" title={t(lang, 'Не удалось загрузить', 'Failed to load')} text={String((query.error as Error).message ?? '')} />
  }
  /* Это загрузка, а не пустота: вращение — единственный видимый признак, что
     запрос ещё идёт, неподвижная надпись неотличима от зависания. */
  if (query.isPending) return <EmptyState icon="loader" title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</span>} />
  return <>{children}</>
}

// ===== Contractor inbox: things the contractor must do ===================
const CKIND: Record<string, { ru: string; icon: string; color: string; bg: string; line: string; cta: string }> = {
  reissue: { ru: 'Переиздать и подать', icon: 'rotate-ccw', color: '--red-500', bg: '--red-50', line: '--red-100', cta: 'Переиздать' },
  answer: { ru: 'Ответить на замечание (ACSR)', icon: 'message-square-reply', color: '--amber-500', bg: '--amber-50', line: '--amber-100', cta: 'Ответить' },
  submit: { ru: 'Подать документ', icon: 'upload', color: '--blue-500', bg: '--blue-50', line: '--blue-100', cta: 'Подать' },
  /* Подтверждение получения — тоже задача: письмо пришло, и заказчик ждёт
     отметки, а не догадки. */
  acknowledge: { ru: 'Подтвердить получение', icon: 'check-check', color: '--green-600', bg: '--green-50', line: '--green-100', cta: 'Подтвердить' },
}

/* Позиция очереди. Форма совпадает с ответом GET /api/inbox: классификацию
   задач ведёт сервер — тем же правилом, что и для стороны заказчика. */
interface CItem {
  kind: string
  ref: string
  /** Предмет задачи: документ, замечание либо трансмиттал (подтверждение). */
  refKind: 'doc' | 'crs' | 'trm'
  title: string
  meta: { disc?: string | null; doc?: string; purpose?: PurposeCode | '—'; code?: ReviewCode | null }
  due: string
  owner: string
  nav: string
}

const EMPTY: CItem[] = []

export function ContractorInbox({
  lang = 'ru',
  vendor,
  onOpenDoc,
  onAnswer,
  onSubmitDoc,
}: {
  lang?: Lang
  vendor: string
  onOpenDoc: (no: string) => void
  onAnswer: (id: string) => void
  onSubmitDoc: (no: string) => void
}) {
  const queue = useSub<CItem[]>('/inbox')
  const items = queue.data ?? EMPTY
  const groups = (['acknowledge', 'reissue', 'answer', 'submit'] as const)
    .map((k) => ({ k, items: items.filter((i) => i.kind === k) }))
    .filter((g) => g.items.length)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead crumbs={<span>{vendor} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Мои задачи', 'My tasks')}</span>} title={t(lang, 'Мои задачи', 'My tasks')} sub={t(lang, 'Что ожидает действий с вашей стороны', 'What needs your action')} count={queue.isSuccess ? items.length : undefined} />
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 32px' }}>
        <Gate query={queue} lang={lang}>
          {groups.length ? (
            groups.map((g) => {
              const k = CKIND[g.k]!
              return (
                <section key={g.k} style={{ marginBottom: 22 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
                    <span style={{ display: 'inline-flex', color: `var(${k.color})` }}><Icon name={k.icon} size={16} /></span>
                    <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, k.ru, k.ru)}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '1px 7px' }}>{g.items.length}</span>
                    <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {g.items.map((it) => (
                      <CTaskCard key={it.ref} it={it} kind={k} lang={lang} onOpenDoc={onOpenDoc} onAnswer={onAnswer} onSubmitDoc={onSubmitDoc} />
                    ))}
                  </div>
                </section>
              )
            })
          ) : (
            <EmptyState icon="check-check" title={t(lang, 'Задач нет', 'All clear')} text={t(lang, 'Все документы поданы, замечаний без ответа нет.', 'Everything submitted, no open comments.')} />
          )}
        </Gate>
      </div>
    </div>
  )
}

function CTaskCard({
  it,
  kind,
  lang,
  onOpenDoc: _onOpenDoc,
  onAnswer,
  onSubmitDoc,
}: {
  it: CItem
  kind: { ru: string; icon: string; color: string; bg: string; line: string; cta: string }
  lang: Lang
  onOpenDoc: (no: string) => void
  onAnswer: (id: string) => void
  onSubmitDoc: (no: string) => void
}) {
  const isC = it.refKind === 'crs'
  const isTrm = it.refKind === 'trm'
  /* Подтверждение получения делается прямо из очереди: задача в том и состоит,
     чтобы расписаться, а не в том, чтобы найти письмо и открыть карточку. */
  const ack = useCommand<{ code: string }>({
    path: (a) => `/transmittals/${encodeURIComponent(a.code)}/acknowledge`,
    body: () => ({}),
    invalidate: () => [qk.all('transmittals'), ...inboxKeys, qk.all('audit')],
  })
  const onClick = () => {
    if (isTrm) {
      ack.mutate({ code: it.ref }, {
        onSuccess: () => toast({ tone: 'success', title: t(lang, 'Получение подтверждено', 'Receipt acknowledged'), text: it.ref }),
      })
      return
    }
    if (isC) onAnswer(it.ref)
    else onSubmitDoc(it.ref)
  }
  return (
    <div className="tdms-actcard tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }} {...clickable(onClick)}>
      <span style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, flex: '0 0 auto', borderRadius: 'var(--radius-sm)', background: `var(${kind.bg})`, color: `var(${kind.color})`, border: `1px solid var(${kind.line})` }}><Icon name={kind.icon} size={17} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-link)' }}>{it.ref}</span>
          {isC ? <CodeChip code={it.meta.code ?? null} size="sm" /> : <>{it.meta.disc ? <DiscTag code={it.meta.disc} /> : null}{it.meta.purpose ? <Purpose value={it.meta.purpose} size="sm" /> : null}</>}
        </div>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 560 }}>{it.title}</div>
        {isC ? <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>{it.meta.doc}</div> : null}
      </div>
      <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7 }}>
        <SlaPill due={it.due} />
        <Button variant="primary" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={(e) => { e.stopPropagation(); onClick() }}>{t(lang, kind.cta, kind.cta)}</Button>
      </div>
    </div>
  )
}

// ===== Contractor documents (scoped register) + submit TRM ==============
export function ContractorDocs({ lang = 'ru', vendor, onOpenDoc }: { lang?: Lang; vendor: string; onOpenDoc: (no: string) => void }) {
  const [page, setPage] = useState(0)
  const [q, setQ] = useState('')
  const [disc, setDisc] = useState('')
  const [life, setLife] = useState('')
  const [slice, setSlice] = useState<Slice>('')
  const filters = {
    ...(disc ? { disc: [disc] } : {}),
    ...(life ? { life: [life] } : {}),
    ...sliceFilters(slice),
  }
  const mine = useMyDocs(page, filters, q)
  const docs = mine.rows
  // Смена отбора сбрасывает страницу: на второй странице нового набора легко
  // оказаться за его пределами.
  const narrow = (apply: () => void) => { apply(); setPage(0) }
  const [selected, setSelected] = useState<Set<React.Key>>(new Set())
  const [building, setBuilding] = useState(false)
  // Подписи титулов и дисциплин — из справочников проекта, а не из копии в памяти.
  const titulRows = useDictRows('titul')
  const discRows = useDictRows('disc')
  const lifeRows = useDictRows('life')
  const discLabels = useDictLabels('disc')
  const lifeLabels = useDictLabels('life')
  const titLbl = (v: string) => {
    const r = titulRows.find((x) => x.code === v)
    return r ? r.code + ' — ' + r.name : v
  }
  const cols: SmartColumn<Doc | Vdoc>[] = [
    { key: 'titul', header: t(lang, 'Титул', 'Title'), width: 64, groupable: true, filterable: true, valueLabel: (v) => titLbl(String(v)), render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--violet-600)' }}>{r.titul}</span> },
    { key: 'mdr', header: 'MDR/VDR', mono: true, nowrap: true, width: 120, groupable: true, filterable: true, value: (r) => registerOf(r), render: (r) => <span style={{ color: 'var(--text-link)' }}>{registerOf(r)}</span> },
    { key: 'no', header: t(lang, '№ документа', 'Document №'), mono: true, nowrap: true, width: 150, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Crit on={'crit' in r && r.crit} /><span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.no}</span></span> },
    { key: 'title', header: t(lang, 'Наименование', 'Title'), render: (r) => <div style={{ minWidth: 0 }}><div style={{ color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{r.title}</div><div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{r.type}</div></div> },
    { key: 'disc', header: t(lang, 'Дисц.', 'Disc.'), width: 60, groupable: true, filterable: true, valueLabel: (v) => String(v) + ' — ' + (discLabels[String(v)] ?? String(v)), render: (r) => <DiscTag code={r.disc} /> },
    { key: 'rev', header: t(lang, 'Рев.', 'Rev'), align: 'center', width: 54, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>{r.rev}</span> },
    { key: 'purpose', header: t(lang, 'Назн.', 'Purpose'), width: 64, filterable: true, render: (r) => <Purpose value={r.purpose} size="sm" /> },
    { key: 'code', header: t(lang, 'Код', 'Code'), align: 'center', width: 52, filterable: true, valueLabel: (v) => v || '—', render: (r) => <CodeChip code={r.code} size="sm" /> },
    { key: 'life', header: t(lang, 'Статус', 'Status'), width: 150, groupable: true, filterable: true, valueLabel: (v) => lifeLabels[String(v)] ?? String(v), render: (r) => <LifeBadge life={r.life} /> },
    { key: 'forecast', header: t(lang, 'Срок', 'Due'), mono: true, align: 'right', width: 100, render: (r) => <SlaPill due={r.forecast} done={r.life === 'afd' || r.life === 'approved' || r.life === 'accepted'} /> },
  ]
  const toggle = (k: React.Key) => {
    const n = new Set(selected)
    if (n.has(k)) n.delete(k)
    else n.add(k)
    setSelected(n)
  }
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead crumbs={<span>{vendor} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Мои документы', 'My documents')}</span>} title={t(lang, 'Мои документы', 'My documents')} count={mine.total}
                actions={<>
                  {/* Выгрузка своей поставки: подрядчик отчитывается тем же
                      файлом, каким получает реестр, и собирать его руками из
                      экрана — работа на полдня. */}
                  <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />}
                          disabled={!docs.length}
                          onClick={() => { exportDocs(docs, 'my-documents.xlsx', lang); noteExport('свои документы', undefined, docs.length) }}>
                    {t(lang, 'Выгрузить', 'Export')}
                  </Button>
                  <Button variant="primary" size="sm" iconLeft={<Icon name="send" size={14} />} disabled={!docs.length} onClick={() => setBuilding(true)}>{t(lang, 'Сформировать трансмиттал', 'Create transmittal')}</Button>
                </>} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Toolbar>
          <div style={{ width: 240 }}>
            <Input size="sm" value={q} onChange={(e) => narrow(() => setQ(e.target.value))} leading={<Icon name="search" size={14} />}
                   placeholder={t(lang, 'Поиск по № или наименованию…', 'Search…')} />
          </div>
          <Select size="sm" value={disc} onChange={(e) => narrow(() => setDisc(e.target.value))} block={false} style={{ width: 150 }}>
            <option value="">{t(lang, 'Все дисциплины', 'All disciplines')}</option>
            {discRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
          </Select>
          <Select size="sm" value={life} onChange={(e) => narrow(() => setLife(e.target.value))} block={false} style={{ width: 170 }}>
            <option value="">{t(lang, 'Любой статус', 'Any status')}</option>
            {lifeRows.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
          </Select>
          {/* Те же срезы, что у заказчика: «что просрочено» и «где нет файла»
              подрядчика касаются даже больше — это его обязательства. */}
          <Select size="sm" value={slice} onChange={(e) => narrow(() => setSlice(e.target.value as Slice))} block={false} style={{ width: 180 }}>
            <option value="">{t(lang, 'Все документы', 'All documents')}</option>
            <option value="overdue">{t(lang, 'Просрочено', 'Overdue')}</option>
            <option value="week">{t(lang, 'Срок на неделе', 'Due this week')}</option>
            <option value="nofile">{t(lang, 'Без файла', 'No file')}</option>
          </Select>
        </Toolbar>
        <div style={{ flex: 1, minHeight: 0 }}>
          <Gate query={mine} lang={lang}>
            <SmartTable name="contractor-docs" columns={cols} rows={docs} rowKey={(r) => r.no} lang={lang} onRowClick={(r) => onOpenDoc(r.no)} selectedKeys={selected} onToggleRow={toggle} onToggleAll={(all) => setSelected(all ? new Set() : new Set(docs.map((d) => d.no)))} defaultGroup="mdr" emptyText={t(lang, 'Нет документов', 'No documents')} />
          </Gate>
        </div>
        <Pager page={page} pageCount={mine.pageCount} total={mine.total} lang={lang} onPage={setPage} />
        {selected.size ? (
          <div className="bulkbar" style={{ position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'var(--slate-900)', color: 'var(--slate-100)', borderTop: '2px solid var(--accent)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{selected.size} {t(lang, 'выбрано', 'selected')}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Button variant="dark" size="sm" iconLeft={<Icon name="x" size={13} />} onClick={() => setSelected(new Set())}>{t(lang, 'Снять', 'Clear')}</Button>
              <Button variant="primary" size="sm" iconLeft={<Icon name="send" size={14} />} onClick={() => setBuilding(true)}>{t(lang, 'Сформировать трансмиттал', 'Create TRM')}</Button>
            </div>
          </div>
        ) : null}
      </div>
      <ContractorTrmBuilder open={building} onClose={() => setBuilding(false)} lang={lang} vendor={vendor} docs={docs} preset={selected} onDone={() => { setBuilding(false); setSelected(new Set()) }} />
    </div>
  )
}

// ===== Contractor TRM builder (creates an incoming TRM) =================
/** Тело команды подачи: отправителя и получателя проставляет сервер по токену. */
interface SubmitArgs { docNos: string[]; medium: string; copies: number }

/** Ответ GET /transmittals/inbound/purposes: куда схема (V97) поведёт документы. */
interface PurposeFlow {
  docs: { no: string; from: string; code: string | null; next: string[] | null; target: string | null; final: boolean }[]
  allowed: string[] | null
}

function ContractorTrmBuilder({
  open,
  onClose,
  lang,
  vendor,
  docs,
  preset,
  onDone,
}: {
  open: boolean
  onClose: () => void
  lang: Lang
  vendor: string
  docs: (Doc | Vdoc)[]
  preset: Set<React.Key>
  onDone: () => void
}) {
  const [picked, setPicked] = useState<Set<React.Key>>(preset && preset.size ? new Set(preset) : new Set())
  /* Назначение — свойство ДОКУМЕНТА, а не пакета: поле выбора убрано, цель
     каждого документа при подаче назначает схема (V97). Сервер отдаёт, куда
     он поведёт каждый документ, — это колонка «Будет назначено». */
  const flow = useSub<PurposeFlow>(
    open && docs.length
      ? '/transmittals/inbound/purposes?docs=' + encodeURIComponent(docs.map((d) => d.no).join(','))
      : null,
  )
  const flowByNo = useMemo(() => {
    const m = new Map<string, PurposeFlow['docs'][number]>()
    for (const row of flow.data?.docs ?? []) m.set(row.no, row)
    return m
  }, [flow.data])
  /* Носитель и число экземпляров описи (V63). Домен принимает их только при
     подаче: поправить потом нельзя — опись правится у черновика, а у входящего
     пакета черновика не бывает. Экран их не спрашивал, и подача бумажного тома
     в двух экземплярах уходила описью «электронно, один». */
  const [medium, setMedium] = useState<'el' | 'paper' | 'both'>('el')
  const [copies, setCopies] = useState('1')
  /* Реестры КОМПАНИИ подрядчика — с сервера (выборка изолирована токеном), а
     не производной от документов: документ, осевший в чужом реестре, приводил
     в меню сам чужой реестр — и подрядчик видел «все MDR» проекта. */
  const myMdrs = useList<Mdr>('mdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] }, { enabled: open })
  const myVdrs = useList<Vdr>('vdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] }, { enabled: open })
  const ownRegs = useMemo(() => new Set<string>([
    ...(myMdrs.data?.rows ?? []).map((m) => m.id),
    ...(myVdrs.data?.rows ?? []).map((v) => v.id),
  ]), [myMdrs.data, myVdrs.data])
  /* Отбор по реестру: документов у подрядчика сотни, а пакет живёт в границах
     одного MDR — выбор реестра сразу сужает список до нужного. */
  const [regFilter, setRegFilter] = useState('')
  const presetRef = useRef(preset)
  presetRef.current = preset
  useEffect(() => {
    // Сброс выбора привязан к открытию диалога: добавление preset в зависимости
    // затирало бы выбор пользователя при любом изменении набора у родителя.
    if (open) setPicked(presetRef.current?.size ? new Set(presetRef.current) : new Set())
  }, [open])

  /* Подача — команда сервера. Он же отбивает документ, который нельзя подать
     (уже в открытом трансмиттале или не в том состоянии): проверять это на
     клиенте значило бы держать второе, расходящееся правило. */
  const submit = useCommand<SubmitArgs, { id?: string; docs?: number }>({
    path: () => '/transmittals/inbound',
    body: (a) => ({ docNos: a.docNos, medium: a.medium, copies: a.copies }),
    invalidate: () => [qk.all('transmittals'), qk.all('docs'), qk.all('vdocs'), ...inboxKeys],
  })

  const toggle = (no: string) => {
    const n = new Set(picked)
    if (n.has(no)) n.delete(no)
    else n.add(no)
    setPicked(n)
  }
  /* Поиск и отбор по реестру сужают список, но не выбор: отмеченный документ,
     скрытый фильтром, остаётся в пакете — счётчик и кнопка считают picked. */
  const [filter, setFilter] = useState('')
  const needle = filter.trim().toLowerCase()
  /* Меню реестров — только СВОИ (с сервера); документ из чужого реестра в
     подачу не попадает вовсе: пакет живёт в границах реестра, и подать его в
     чужой всё равно нельзя. Такие документы — рассинхрон данных, их видно на
     экране «Мои документы», но не здесь. */
  const registers = useMemo(() => [...ownRegs].sort(), [ownRegs])
  const shown = docs.filter((d) =>
    ownRegs.has(registerOf(d))
    && (!regFilter || registerOf(d) === regFilter)
    && (!needle || (d.no + ' ' + d.title).toLowerCase().includes(needle)))
  /* Пакет живёт в границах одного реестра — то же правило держит сервер
     (409 trm.mixed-registers). Первый отмеченный документ выбирает реестр,
     остальные реестры блокируются, а не отбиваются постфактум. */
  const firstPicked = docs.find((d) => picked.has(d.no))
  const pickedRegister = firstPicked ? registerOf(firstPicked) : null
  const pickedDocs = docs.filter((d) => picked.has(d.no)).map((d) => ({ no: d.no, rev: d.rev }))
  const send = () => {
    submit.mutate({
      docNos: [...picked] as string[],
      medium,
      /* Домен принимает от 1 до 999 и отбивает остальное: здесь значение
         приводится, чтобы пустое поле не уезжало нулём. */
      copies: Math.min(999, Math.max(1, Number.parseInt(copies, 10) || 1)),
    }, {
      onSuccess: (result) => {
        // Номер выделяет сервер, поэтому он приходит в ответе; называть его
        // важно — по нему подрядчик потом ищет пакет в переписке.
        const trm = result as { id?: string; docs?: number }
        toast({
          tone: 'success',
          title: trm?.id ? `Трансмиттал ${trm.id} подан` : 'Трансмиттал подан',
          text: `Документов в пакете: ${trm?.docs ?? picked.size}. Ожидает входного контроля.`,
        })
        onDone()
      },
    })
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <div>
          <div>{t(lang, 'Сформировать входящий трансмиттал', 'Create incoming transmittal')}</div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', fontWeight: 400, marginTop: 2 }}>{t(lang, 'Выберите документы — пакет уйдёт заказчику на рассмотрение', 'Pick documents — the package goes to the owner')}</div>
        </div>
      }
    >
      <div style={{ width: 720, maxWidth: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <Field label={t(lang, 'Отправитель', 'From')}>{vendor}</Field>
          {/* Назначение у пакета больше не выбирается: цель — свойство
              документа, и при подаче её назначает схема (колонка «Будет
              назначено» ниже). Вместо него — отбор по реестру MDR. */}
          <Select label={t(lang, 'Реестр MDR', 'MDR register')} value={regFilter}
                  onChange={(e) => setRegFilter(e.target.value)}
                  hint={t(lang, 'Пакет собирается в границах одного реестра', 'A package stays within one register')}>
            <option value="">{t(lang, '— все реестры —', '— all registers —')}</option>
            {registers.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Select label={t(lang, 'Носитель', 'Medium')} value={medium} onChange={(e) => setMedium(e.target.value as 'el' | 'paper' | 'both')}>
            <option value="el">{t(lang, 'Электронно', 'Electronic')}</option>
            <option value="paper">{t(lang, 'Бумага', 'Paper')}</option>
            <option value="both">{t(lang, 'Бумага и эл.', 'Paper & el.')}</option>
          </Select>
          <Input label={t(lang, 'Экземпляров', 'Copies')} value={copies} onChange={(e) => setCopies(e.target.value)}
                 hint={t(lang, 'Печатается в описи бланка', 'Printed in the blank composition')} />
        </div>
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{t(lang, 'Документы', 'Documents')}</span>
            <div style={{ width: 240 }}><Input size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} leading={<Icon name="search" size={13} />} placeholder={t(lang, 'Поиск по документу…', 'Search documents…')} /></div>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{picked.size} {t(lang, 'выбрано', 'selected')}</span>
          </div>
          {/* Шапка колонок: без подписи колонка «→» читалась как украшение. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
            <span style={{ width: 16, flex: '0 0 auto' }} />
            <span style={{ width: 150, flex: '0 0 auto' }}>{t(lang, 'Документ', 'Document')}</span>
            <span style={{ flex: 1 }}>{t(lang, 'Наименование', 'Title')}</span>
            <span style={{ width: 84, flex: '0 0 auto' }}>{t(lang, 'Реестр', 'Register')}</span>
            <span style={{ width: 34, flex: '0 0 auto' }}>{t(lang, 'Рев.', 'Rev')}</span>
            <span style={{ width: 52, flex: '0 0 auto' }}>{t(lang, 'Назн.', 'Purpose')}</span>
            <span style={{ width: 92, flex: '0 0 auto' }} title={t(lang, 'Какое назначение схема присвоит документу при отправке', 'The purpose the scheme will assign on submission')}>{t(lang, 'Будет назначено', 'Will become')}</span>
          </div>
          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            {shown.map((d) => {
              const reg = registerOf(d)
              const blocked = pickedRegister != null && reg !== pickedRegister && !picked.has(d.no)
              /* «Будет назначено» — расчёт сервера по схеме (V97): им подача и
                 руководствуется. Финал — подать нельзя, нужна новая ревизия. */
              const f = flowByNo.get(d.no)
              return (
                <label key={d.no} className="tdms-rail" title={blocked ? t(lang, `Пакет собирается в границах ${pickedRegister}: документы разных реестров не смешиваются`, `The package stays within ${pickedRegister}: registers cannot be mixed`) : undefined}
                       style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.45 : 1 }}>
                  <Checkbox checked={picked.has(d.no)} disabled={blocked} onChange={() => toggle(d.no)} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)', width: 150, flex: '0 0 auto' }}>{d.no}</span>
                  <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)', width: 84, flex: '0 0 auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>{reg}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, width: 34, flex: '0 0 auto' }}>{d.rev}</span>
                  <span style={{ width: 52, flex: '0 0 auto' }}><Purpose value={d.purpose} size="sm" /></span>
                  <span style={{ width: 92, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {f?.final ? (
                      <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }} title={t(lang, 'Схема пройдена: документ согласован, продолжение — новой ревизией', 'The scheme is complete: the document is approved, continue with a new revision')}>{t(lang, 'финал', 'final')}</span>
                    ) : f?.target && f.target !== d.purpose ? (
                      <>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>→</span>
                        <Purpose value={f.target} size="sm" />
                      </>
                    ) : (
                      <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'без смены', 'unchanged')}</span>
                    )}
                  </span>
                </label>
              )
            })}
            {docs.length > 0 && shown.length === 0 ? (
              <div style={{ padding: '14px 12px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Ничего не найдено', 'Nothing found')}</div>
            ) : null}
          </div>
        </div>
        <TrmFilesZone docs={pickedDocs} lang={lang} disabled={submit.isPending} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" iconLeft={<Icon name="send" size={14} />} disabled={!picked.size || submit.isPending} onClick={send}>
            {submit.isPending ? t(lang, 'Отправка…', 'Sending…') : `${t(lang, 'Отправить заказчику', 'Send to owner')} · ${picked.size}`}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ===== Contractor transmittals (scoped) =================================
const TRM_STATUS: Record<string, Tone> = { Зарегистрирован: 'info', Проверен: 'info', Принят: 'accent', 'На рассмотрении': 'accent', Замечания: 'warning', Отправлен: 'info', Закрыт: 'success', Отклонён: 'danger' }
export function ContractorTrm({ lang = 'ru', vendor, onOpenTrm }: { lang?: Lang; vendor: string; onOpenTrm?: (id: string) => void }) {
  /* Постранично, с поиском и выгрузкой — как журнал у заказчика: у подрядчика
     по крупному контракту сотни писем, и «первая сотня» означала бы, что
     остальных нет. */
  const [page, setPage] = useState(0)
  const [q, setQ] = useState('')
  const query = useList<Transmittal>('transmittals',
    { page, pageSize: PAGE, sort: [{ id: 'id', desc: true }], q: q || undefined })
  const rows = query.data?.rows ?? []
  /* Список документов здесь нужен только конструктору трансмиттала: подрядчик
     выбирает из него состав пакета. Отбор ему тот же, что на «моих
     документах» — первая страница без сужения. */
  const mine = useMyDocs(0, {}, '')
  const [building, setBuilding] = useState(false)
  const cols: SmartColumn<Transmittal>[] = [
    {
      key: 'dir',
      header: '',
      width: 40,
      render: (r) => {
        const inb = r.dir === 'in'
        return <span title={inb ? 'Отправлен заказчику' : 'Получен от заказчика'} style={{ display: 'inline-grid', placeItems: 'center', width: 24, height: 24, borderRadius: 'var(--radius-sm)', background: inb ? 'var(--amber-50)' : 'var(--blue-50)', color: inb ? 'var(--amber-600)' : 'var(--blue-600)', border: '1px solid ' + (inb ? 'var(--amber-100)' : 'var(--blue-100)') }}><Icon name={inb ? 'arrow-up-right' : 'arrow-down-left'} size={14} /></span>
      },
      value: (r) => (r.dir === 'in' ? 'Исходящий (мой)' : 'Входящий (ответ ДО)'),
      groupable: true,
      filterable: true,
    },
    { key: 'id', header: '№ TRM', mono: true, width: 96, render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{r.id}</span> },
    { key: 'subject', header: t(lang, 'Содержание', 'Subject'), render: (r) => <div><div style={{ color: 'var(--text-body)' }}>{r.subject}</div><div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{r.from} → {r.to}</div></div> },
    { key: 'purpose', header: t(lang, 'Назн.', 'Purpose'), width: 60, filterable: true, render: (r) => <Purpose value={r.purpose} size="sm" /> },
    { key: 'docs', header: t(lang, 'Док.', 'Docs'), align: 'center', mono: true, width: 56 },
    { key: 'date', header: t(lang, 'Дата', 'Date'), mono: true, align: 'right', width: 86, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.date}</span> },
    { key: 'status', header: t(lang, 'Статус', 'Status'), width: 150, groupable: true, filterable: true, render: (r) => <Badge tone={TRM_STATUS[r.status] ?? 'neutral'} size="sm" dot>{r.status}</Badge> },
    { key: 'code', header: t(lang, 'Код', 'Code'), align: 'center', width: 50, filterable: true, valueLabel: (v) => v || '—', render: (r) => <CodeChip code={r.code} size="sm" /> },
  ]
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead crumbs={<span>{vendor} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Трансмитталы', 'Transmittals')}</span>} title={t(lang, 'Мои трансмитталы', 'My transmittals')} count={query.data?.total}
                actions={<>
                  <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />}
                          onClick={() => { exportTransmittals(rows, 'my-transmittals.xlsx', lang); noteExport('свои трансмитталы', undefined, rows.length) }}>
                    {t(lang, 'Выгрузить', 'Export')}
                  </Button>
                  <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setBuilding(true)}>{t(lang, 'Сформировать TRM', 'Create TRM')}</Button>
                </>} />
      <Toolbar>
        <div style={{ width: 280 }}>
          <Input size="sm" value={q} onChange={(e) => { setQ(e.target.value); setPage(0) }} leading={<Icon name="search" size={14} />}
                 placeholder={t(lang, 'Поиск по № TRM и содержанию…', 'Search by TRM number or subject…')} />
        </div>
      </Toolbar>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Gate query={query} lang={lang}>
          <SmartTable name="contractor-trm" columns={cols} rows={rows} rowKey={(r) => r.id} lang={lang} onRowClick={(r) => onOpenTrm && onOpenTrm(r.id)} defaultGroup="dir" emptyText={t(lang, 'Нет трансмитталов', 'No transmittals')} />
        </Gate>
      </div>
      <Pager page={query.data?.page ?? 0} pageCount={query.data?.pageCount ?? 1} total={query.data?.total ?? 0} lang={lang} onPage={setPage} />
      <ContractorTrmBuilder open={building} onClose={() => setBuilding(false)} lang={lang} vendor={vendor} docs={mine.rows} preset={new Set()} onDone={() => setBuilding(false)} />
    </div>
  )
}

// ===== Contractor comments (answer ACSR) ================================
/* Экран читается так же, как у заказчика: документ → лист CRS → замечания
   листа. Прежде подрядчику отдавался один плоский список всех замечаний
   проекта — лист, которым они пришли, на экране не существовал, хотя отвечают
   именно на лист, и он же приложен к трансмитталу. Дерево и выборка общие
   (`useCrsSheets`), разное только то, что подрядчик делает с замечанием:
   отвечает, а не закрывает. */
export function ContractorCrs({ lang = 'ru', vendor, focus }: { lang?: Lang; vendor: string; focus?: string }) {
  const [tab, setTab] = useState('open')
  const [printing, setPrinting] = useState<string | null>(null)
  const state = useCrsSheets(tab, focus)
  const { sheet, counts } = state
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead crumbs={<span>{vendor} <span style={{ color: 'var(--border-strong)' }}>/</span> CRS / ACSR</span>}
                title={t(lang, 'Замечания заказчика', 'Owner comments')} count={counts.all}
                tabsNode={<CrsTabs value={tab} onChange={setTab} tabs={crsSheetTabs(lang, counts)} />} />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <CrsSheetRail lang={lang} state={state} />
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 24 }}>
          {sheet ? (
            <VendorSheetPane sheet={sheet} lang={lang} focus={focus} onPrint={() => setPrinting(sheet.id)} />
          ) : (
            /* Спиннер — только в ветке загрузки: настоящее «замечаний нет»
               ничего не ждёт, и крутиться там нечему. */
            <EmptyState icon="message-square" title={state.query.isPending
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</span>
              : t(lang, 'Замечаний нет', 'No comments')} />
          )}
        </div>
      </div>
      <SheetPrint code={printing} lang={lang} onClose={() => setPrinting(null)} />
    </div>
  )
}

/** Вкладки листов — подчёркнутая полоса в шапке, как на остальных экранах. */
function CrsTabs({ tabs, value, onChange }: { tabs: { id: string; label: ReactNode; count?: number }[]; value: string; onChange: (id: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border-default)', marginBottom: -1 }}>
      {tabs.map((tb) => (
        <button key={tb.id} onClick={() => onChange(tb.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 12px', border: 0, borderBottom: '2px solid ' + (tb.id === value ? 'var(--accent)' : 'transparent'), background: 'transparent', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: tb.id === value ? 600 : 500, color: tb.id === value ? 'var(--text-strong)' : 'var(--text-muted)', cursor: 'pointer',
                  /* Как у PtTabs и .tabs__trigger: цвет и подчёркивание переезжают вместе. */
                  transition: 'color var(--duration-fast) var(--ease-standard), border-bottom-color var(--duration-fast) var(--ease-standard)' }}>
          {tb.label}
          {tb.count === undefined ? null : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{tb.count}</span>}
        </button>
      ))}
    </div>
  )
}

/* Лист со стороны подрядчика: шапка листа и его замечания. Каждое раскрывается
   в переписку — замечание заказчика и свой ответ. */
function VendorSheetPane({ sheet, lang, focus, onPrint }: { sheet: CrsSheetRow; lang: Lang; focus?: string; onPrint: () => void }) {
  const [expanded, setExpanded] = useState<string>(focus ?? '')
  useEffect(() => { setExpanded(focus ?? '') }, [focus, sheet.id])
  const [answering, setAnswering] = useState<Crs | null>(null)
  const itemsQuery = useSheetItems(sheet.id)
  const items = itemsQuery.data?.rows ?? []
  const client = useQueryClient()
  const [loading, setLoading] = useState(false)

  /* Заполненный бланк уходит на сервер целиком: он его печатал, он и разберёт.
     Отчёт показывается как у импорта реестра — что легло и что пропущено. */
  const pickFilledBlank = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsm,.xlsx'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      setLoading(true)
      try {
        const result = await uploadForm<{ answered: number; skipped: number }>(
          `/crs-sheets/${encodeURIComponent(sheet.id)}/form`, file,
          { idempotencyKey: crypto.randomUUID() })
        await Promise.all([qk.all('crs'), ...inboxKeys].map((key) => client.invalidateQueries({ queryKey: key })))
        await itemsQuery.refetch()
        toast({ tone: result.skipped ? 'info' : 'success',
                title: t(lang, 'Ответы загружены', 'Answers loaded'),
                text: t(lang, `принято ${result.answered}, пропущено ${result.skipped}`,
                              `accepted ${result.answered}, skipped ${result.skipped}`) })
      } catch (e) {
        toast({ tone: 'danger', title: t(lang, 'Бланк не загружен', 'Blank not loaded'),
                text: e instanceof Error ? e.message : String(e) })
      } finally {
        setLoading(false)
      }
    }
    input.click()
  }

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Вход переписки — классом: @starting-style не выразить инлайновым
          стилем React (приём .toast). Уход мгновенный — паритет с Comments. */}
      <style>{`.ct-thread { transition: opacity var(--duration-fast) var(--ease-out); } @starting-style { .ct-thread { opacity: 0; } }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <CodeChip code={sheet.code} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{sheet.id}</h2>
            {sheet.open
              ? <Badge tone="danger" size="sm" dot>{t(lang, 'без ответа', 'unanswered')} {sheet.open}</Badge>
              : <Badge tone="success" size="sm" dot>{t(lang, 'отработан', 'settled')}</Badge>}
            <Button variant="secondary" size="sm" style={{ marginLeft: 'auto' }} iconLeft={<Icon name="printer" size={13} />} onClick={onPrint}>
              {t(lang, 'Печать листа', 'Print sheet')}
            </Button>
            {/* Ответ в том же файле, который пришёл: так заведено и в ASSAI, и
                на бумаге. Разбирает бланк сервер — раскладку колонок знает он,
                он же её и печатал. */}
            <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={13} />} disabled={loading}
                    title={t(lang, 'Загрузить бланк CRS с ответами: ответы лягут на замечания листа',
                                  'Upload the filled CRS blank: the answers land on the sheet comments')}
                    onClick={pickFilledBlank}>
              {loading ? t(lang, 'Загружаем…', 'Uploading…') : t(lang, 'Загрузить ответы', 'Upload answers')}
            </Button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, padding: '12px 14px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <Field label={t(lang, 'Документ', 'Document')} mono>{sheet.doc}</Field>
            <Field label={t(lang, 'Ревизия и подача', 'Revision and submission')} mono>{revVer(sheet.rev, sheet.ver)}</Field>
            <Field label={t(lang, 'Дисциплина', 'Discipline')}>{sheet.disc ?? '—'}</Field>
            {/* Трансмиттал, которым лист пришёл: подрядчик ищет замечания
                по своей входящей почте, а не по номеру рассмотрения. */}
            <Field label={t(lang, 'Пришёл трансмитталом', 'Delivered by')} mono>{sheet.trmOut ?? '—'}</Field>
            <Field label={t(lang, 'Подготовил', 'Prepared by')}>{sheet.preparedBy}</Field>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Замечания листа', 'Comments in the sheet')}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{sheet.items}</span>
      </div>

      {itemsQuery.isPending ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
      ) : items.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((c, i) => (
            <VendorCommentRow key={c.id} n={i + 1} crs={c} lang={lang} open={c.id === expanded}
                              onToggle={() => setExpanded(expanded === c.id ? '' : c.id)}
                              onAnswer={() => setAnswering(c)} />
          ))}
        </div>
      ) : (
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
          {t(lang, 'В листе нет замечаний', 'The sheet has no comments')}
        </div>
      )}
      {answering ? <AcsrDialog open onClose={() => setAnswering(null)} lang={lang} crs={answering} /> : null}
    </div>
  )
}

/** Позиция листа: свёрнутая — строка, развёрнутая — переписка и ответ. */
function VendorCommentRow({ n, crs, lang, open, onToggle, onAnswer }: {
  n: number
  crs: Crs
  lang: Lang
  open: boolean
  onToggle: () => void
  onAnswer: () => void
}) {
  return (
    <div style={{ border: '1px solid ' + (open ? 'var(--accent-line)' : 'var(--border-default)'), borderRadius: 'var(--radius-md)', background: 'var(--surface-card)', overflow: 'hidden', transition: 'border-color var(--duration-fast) var(--ease-standard)' }}>
      <button onClick={onToggle} className="tdms-rail"
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', border: 0, background: 'transparent', cursor: 'pointer' }}>
        <span style={{ display: 'grid', placeItems: 'center', width: 20, height: 20, flex: '0 0 auto', borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{n}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-strong)' }}>{crs.id}</span>
        <CritBadge level={crs.crit} lang={lang} />
        <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: open ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{crs.text}</span>
        {crs.status === 'open'
          ? <Badge tone="danger" size="sm" dot>{t(lang, 'Нужен ответ', 'Answer due')}</Badge>
          : crs.status === 'closed'
            ? <Badge tone="neutral" size="sm" dot>{t(lang, 'Закрыто', 'Closed')}</Badge>
            : <Badge tone="success" size="sm" dot>{t(lang, 'Отвечено', 'Answered')}</Badge>}
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
      </button>
      {open ? (
        <div className="ct-thread" style={{ padding: '10px 14px 14px', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '4px 0 14px' }}>
            {/* Адрес выпуска — и на стороне подрядчика: без него он не
                понимает, на какую присылку замечание и отвечал ли уже. */}
            <Field label={t(lang, 'Выпущено на', 'Raised on')} mono>{revVer(crs.rev, crs.ver)}</Field>
            <Field label={t(lang, 'Стр.', 'Page')} mono>{crs.page || '—'}</Field>
            <Field label={t(lang, 'Пункт НТД', 'Clause')}>{crs.clause || '—'}</Field>
            <Field label={t(lang, 'Срок ответа', 'Response due')}><SlaPill due={crs.due} done={crs.status !== 'open'} /></Field>
          </div>
          <Bubble author={crs.author} role={t(lang, 'Заказчик · замечание', 'Owner · comment')} date={crs.date} title="CRS" tone="--amber-400">{crs.text}</Bubble>
          {crs.acsr ? <Bubble author={crs.acsr.author} role={t(lang, 'Ваш ответ', 'Your response')} date={crs.acsr.date} title="ACSR" tone="--blue-500" disp={crs.acsr.disp}>{crs.acsr.text}</Bubble> : null}
          {/* Причина возврата: раньше ответ просто снова становился «ожидается»,
              и подрядчик не знал, что именно не так. */}
          {crs.reworkReason ? (
            <div style={{ marginLeft: 44, marginTop: 8, padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--warning-ink)' }}>
              {t(lang, 'Ответ возвращён на доработку', 'Response returned for rework')}: {crs.reworkReason}
            </div>
          ) : null}
          <div style={{ marginTop: 14, marginLeft: 44, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
            {crs.status === 'open' ? (
              <Button variant="primary" size="sm" iconLeft={<Icon name="message-square-reply" size={14} />} onClick={onAnswer}>{t(lang, 'Подготовить ответ (ACSR)', 'Prepare response (ACSR)')}</Button>
            ) : crs.status === 'closed' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--success-ink)' }}><Icon name="check-check" size={16} />{t(lang, 'Замечание закрыто заказчиком', 'Closed by owner')}</span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}><Icon name="clock" size={15} />{t(lang, 'Ответ отправлен, ожидает проверки заказчиком', 'Response sent, awaiting owner review')}</span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Bubble({
  author,
  role,
  date,
  title,
  tone,
  disp,
  children,
}: {
  author: string
  role: ReactNode
  date: string
  title: string
  tone: string
  disp?: string
  children: ReactNode
}) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
      <Avatar name={author} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>{author}</span>
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{role}</span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{disp ? <Badge tone="warning" size="sm">{disp}</Badge> : null}<span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: `var(${tone})`, letterSpacing: '.06em' }}>{title}</span></span>
        </div>
        <div style={{ padding: '12px 14px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderLeft: `3px solid var(${tone})`, borderRadius: 'var(--radius-md)', font: 'var(--font-body)', fontSize: 'var(--text-md)', color: 'var(--text-body)' }}>{children}<div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>{date}</div></div>
      </div>
    </div>
  )
}

/** Степень согласия подрядчика — три кода листа CRS, как в бланке заказчика. */
const AGREED: { code: AgreedCode; ru: string; en: string }[] = [
  { code: '1', ru: 'Согласовано, будет откорректировано полностью', en: 'Agreed / fully implemented' },
  { code: '2', ru: 'Согласовано, будет откорректировано частично', en: 'Partly agreed / partly implemented' },
  { code: '3', ru: 'Отклонено', en: 'Rejected / not implemented' },
]

function AcsrDialog({ open, onClose, lang, crs }: { open: boolean; onClose: () => void; lang: Lang; crs: Crs }) {
  const [text, setText] = useState('')
  /* Колонки листа CRS: имя рассматривающего (организация подписывается сама,
     а бланк спрашивает человека), степень согласия и ссылка на протокол. */
  const [reviewer, setReviewer] = useState('')
  const [agreed, setAgreed] = useState<AgreedCode>('1')
  const [reference, setReference] = useState('')
  /* Автора ответа берёт сервер из токена. Раньше он приходил аргументом —
     то есть подписаться можно было кем угодно. */
  const answer = useCommand<{ code: string; body: string; reviewer?: string; agreed: AgreedCode; reference?: string }>({
    path: (a) => `/crs/${encodeURIComponent(a.code)}/acsr`,
    body: (a) => ({ body: a.body, reviewer: a.reviewer, agreed: a.agreed, reference: a.reference }),
    invalidate: () => [qk.all('crs'), ...inboxKeys],
  })
  const submit = () => {
    answer.mutate({ code: crs.id, body: text.trim(), reviewer: reviewer.trim() || undefined,
      agreed, reference: reference.trim() || undefined }, {
      onSuccess: () => {
        onClose()
        setText('')
        setReference('')
      },
    })
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <div>
          <div>{t(lang, 'Ответ на замечание (ACSR)', 'Response to comment (ACSR)')}</div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', fontWeight: 400, marginTop: 2 }}>{crs.id + ' · ' + crs.doc}</div>
        </div>
      }
    >
      <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', marginBottom: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}><b style={{ color: 'var(--text-body)' }}>CRS:</b> {crs.text}</div>
      <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Текст ответа', 'Response text')}</label>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t(lang, 'Опишите, как учтено замечание…', 'Describe how the comment was addressed…')} style={{ width: '100%', minHeight: 96, padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', color: 'var(--text-body)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', resize: 'vertical', boxSizing: 'border-box' }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
        <Select label={t(lang, 'Степень согласия', 'Agreed')} value={agreed}
                onChange={(e) => setAgreed(e.target.value as AgreedCode)}
                hint={t(lang, 'Колонка листа CRS', 'CRS sheet column')}>
          {AGREED.map((a) => <option key={a.code} value={a.code}>{a.code} — {t(lang, a.ru, a.en)}</option>)}
        </Select>
        <Input label={t(lang, 'Рассматривающий', 'Reviewer')} value={reviewer}
               onChange={(e) => setReviewer(e.target.value)}
               placeholder={t(lang, 'Имя специалиста подрядчика', 'Contractor specialist name')} />
        <div style={{ gridColumn: '1 / -1' }}>
          <Input label={t(lang, 'Ссылка на протокол или письмо', 'Ref to MOM or letter')} value={reference}
                 onChange={(e) => setReference(e.target.value)}
                 placeholder={t(lang, 'Если ответ на них опирается', 'If the answer relies on one')} />
        </div>
      </div>
      {/* Приложение к ответу: исправленный фрагмент, расчёт, письмо. Файл
          уходит в хранилище тем же трёхшаговым обменом, что и документ, —
          записи о несуществующем файле здесь больше нет. Переизданный
          ДОКУМЕНТ по-прежнему грузится в карточке: это ревизия, а не
          приложение к переписке. */}
      <div style={{ marginTop: 14 }}>
        <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>
          {t(lang, 'Приложения к ответу', 'Attachments')}
        </div>
        <CrsFilesZone code={crs.id} lang={lang} kind="answer" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" iconLeft={<Icon name="send" size={14} />} disabled={!text.trim() || answer.isPending} onClick={submit}>
          {answer.isPending ? t(lang, 'Отправка…', 'Sending…') : t(lang, 'Отправить ответ', 'Send response')}
        </Button>
      </div>
    </Dialog>
  )
}
